/**
 * Manager Offers Section Component
 * Handles offers/benefits management for managers
 */
import React from 'react';
import { Gift, Plus, Pencil, Trash2 } from 'lucide-react';
import { Button } from '../ui/button';

export function ManagerOffersSection({
    offers,
    onOpenNewModal,
    onEdit,
    onDelete,
    isResidential
}) {
    const buttonClass = isResidential ? 'bg-stone-600 hover:bg-stone-700' : 'bg-emerald-600 hover:bg-emerald-700';
    
    return (
        <div className="space-y-6" data-testid="manager-offers">
            <div className="flex justify-between items-center">
                <h2 className="text-2xl font-bold">
                    {isResidential ? 'Member Benefits' : 'Offers Management'}
                </h2>
                <Button onClick={onOpenNewModal} className={buttonClass} data-testid="manager-add-offer-btn">
                    <Plus className="w-4 h-4 mr-2" />
                    Add {isResidential ? 'Benefit' : 'Offer'}
                </Button>
            </div>

            <div className="text-sm text-muted-foreground">
                {offers.length} {isResidential ? 'benefits' : 'offers'} total
            </div>
            
            <div className="space-y-3">
                {offers.map(offer => (
                    <div key={offer.id} className="card-base" data-testid={`manager-offer-card-${offer.id}`}>
                        <div className="flex flex-col md:flex-row md:items-center justify-between gap-4">
                            <div className="flex-1">
                                <div className="flex flex-wrap items-center gap-2 mb-2">
                                    <span className={`text-xs font-bold px-2 py-1 rounded-full ${
                                        offer.category === 'restaurant' ? 'bg-amber-100 text-amber-700' :
                                        offer.category === 'plants' ? 'bg-green-100 text-green-700' :
                                        offer.category === 'delivery' ? 'bg-blue-100 text-blue-700' :
                                        offer.category === 'wellness' ? 'bg-teal-100 text-teal-700' :
                                        offer.category === 'dining' ? 'bg-rose-100 text-rose-700' :
                                        offer.category === 'services' ? 'bg-purple-100 text-purple-700' :
                                        'bg-gray-100 text-gray-600'
                                    }`}>
                                        {offer.category || 'General'}
                                    </span>
                                    {offer.is_members_only && (
                                        <span className="text-xs font-bold px-2 py-1 rounded-full bg-primary/10 text-primary">
                                            Members Only
                                        </span>
                                    )}
                                    {offer.discount_type === 'percentage' && offer.discount_value > 0 && (
                                        <span className="text-xs font-bold px-2 py-1 rounded-full bg-red-100 text-red-700">
                                            {offer.discount_value}% OFF
                                        </span>
                                    )}
                                    {!offer.is_active && (
                                        <span className="text-xs font-bold px-2 py-1 rounded-full bg-gray-200 text-gray-600">
                                            Inactive
                                        </span>
                                    )}
                                </div>
                                <p className="font-bold text-lg">{offer.title}</p>
                                <p className="text-sm text-muted-foreground line-clamp-2">{offer.description}</p>
                                {offer.valid_until && (
                                    <p className="text-xs text-muted-foreground mt-2">
                                        Valid until: {new Date(offer.valid_until).toLocaleDateString('en-GB', {
                                            day: 'numeric',
                                            month: 'short',
                                            year: 'numeric'
                                        })}
                                    </p>
                                )}
                            </div>
                            <div className="flex gap-2 flex-shrink-0">
                                <Button 
                                    variant="outline" 
                                    size="sm" 
                                    onClick={() => onEdit(offer)}
                                    data-testid={`manager-edit-offer-${offer.id}`}
                                    title={`Edit ${isResidential ? 'Benefit' : 'Offer'}`}
                                >
                                    <Pencil className="w-4 h-4" />
                                </Button>
                                <Button 
                                    variant="destructive" 
                                    size="sm" 
                                    onClick={() => onDelete(offer)}
                                    data-testid={`manager-delete-offer-${offer.id}`}
                                    title={`Delete ${isResidential ? 'Benefit' : 'Offer'}`}
                                >
                                    <Trash2 className="w-4 h-4" />
                                </Button>
                            </div>
                        </div>
                    </div>
                ))}
                
                {offers.length === 0 && (
                    <div className="card-base text-center py-12">
                        <Gift className="w-12 h-12 text-muted-foreground mx-auto mb-3" />
                        <p className="text-muted-foreground">
                            No {isResidential ? 'benefits' : 'offers'} yet
                        </p>
                        <Button onClick={onOpenNewModal} className={`mt-4 ${buttonClass}`}>
                            Create First {isResidential ? 'Benefit' : 'Offer'}
                        </Button>
                    </div>
                )}
            </div>
        </div>
    );
}
